// React
import React, { useEffect, useMemo, useCallback, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

// Components
import {
  Dialog,
  MpsButton,
  MpsCheckBox,
  MpsSectionedForm
} from '@mobilitystore/components-interface';
import Link from '../../../msreact-kit/html-components/link';
import { ChartGrid } from '@mobilitystore/charts-interface';
import CustomCell from '../../../msreact-kit/html-components/custom-cell';

// Data
import usePlaceOrderFormStore from '../../../store/place-order-store';
import { placeOrderFormData } from '../../../data/place-order-form-data';
import { FieldConstants } from '../../../shared/constants/field-constants';
import idConstants, { REGION, PRODUCT_USAGE } from '../../../data/idConstants';
import AccessDenied from '../../Shared/AccessDenied';
import { accessRoles } from '../../../data/appConstants';

// Assets
import { CDN_LINK } from '../../../assets/img';

//Utils
import { getDeviceClass, getBillingTypeCodeFromPlanType } from '../../../utils';
import Loader from '../../Shared/Utils/Loader';

// Actions
import { getBuyerProducts } from '../../../actions/product-search-service-action';
import { AAA_CONFIG } from '../../../actions/configuration';
import {
  getApprovedBuyersList,
  getCompanyDetailsBySearchTerm,
  getCompanyRegistrationDetails
} from '../../../actions/user-service-action';
import { getPricingPolicies } from '../../../actions/api/OrderSummaryAction';
import { getPricingDetailsByProductIdList } from '../../../actions/api/pricing-plan-action';
import {
  getCartProductDetails,
  getProductOrderId,
  saveCheckoutId
} from '../../../actions/orchestration-service-action';
import {
  processAddressCheckout,
  createCheckoutId,
  initiateOrderProcess,
  updateCartProduct,
  getCartProduct,
  deleteCartProducts
} from '../../../actions/order-service-action';
import { createOrderSubscription } from '../../../actions/order-fulfilment-service';
import { REGION_CODE } from '../../../actions/content-action';
import {
  acceptUserAgreementTerms,
  logout,
  checkUserAgreementAcceptance
} from '../../../actions/LoginAction';

// CSS
import './index.scss';

// Additional Information Cell Renderer Component with newline support
const AdditionalInfoCellRenderer = ({ value }) => {
  return (
    <div
      style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: '1.5' }}
    >
      {value || ''}
    </div>
  );
};

AdditionalInfoCellRenderer.propTypes = {
  value: PropTypes.string
};

// Actions Cell Renderer Component
const ActionsCellRenderer = ({ data, onDelete }) => {
  return (
    <div className='bulk-order-table__action'>
      <button
        type='button'
        className='delete-product-icon'
        onClick={() => onDelete(data.productId, data.planId)}
        aria-label='Delete'
      >
        <img src={`${CDN_LINK}/delete.svg`} alt='Delete' />
      </button>
    </div>
  );
};

ActionsCellRenderer.propTypes = {
  data: PropTypes.shape({
    productId: PropTypes.string,
    planId: PropTypes.string
  }).isRequired,
  onDelete: PropTypes.func.isRequired
};

const PlaceOrder = () => {
  // Track last changed field to prevent cart deletion on Add Product
  const lastChangedFieldRef = useRef(null);
  const history = useHistory();
  const { placeOrderForm, setPlaceOrderForm } = usePlaceOrderFormStore();
  const dispatch = useDispatch();
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [isProcessingOrder, setIsProcessingOrder] = useState(false);
  const [showConsent, setShowConsent] = useState(false);
  const [isConsentChecked, setIsConsentChecked] = useState(false);
  const [addedProducts, setAddedProducts] = useState([]);
  const [cartId, setCartId] = useState(null);
  const lastCustomerApiCallRef = useRef(null);
  const lastPricingApiCallRef = useRef(null);
  const lastProductApiCallRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const searchActiveRef = useRef(false); // Tracks if company search has been triggered to avoid initial-load restores
  const prevPaymentTypeRef = useRef(undefined); // Use undefined to distinguish initial state from null values
  const deletedCartIdsRef = useRef(new Set()); // Track deleted cart IDs to prevent duplicate deletions
  const cartIdRef = useRef(null); // Track latest cartId for cleanup
  const userIdRef = useRef(null); // Track latest userId for cleanup
  const deviceClass = getDeviceClass();

  // Placeholder option for dropdowns
  const PLACEHOLDER_OPTION = {
    id: null,
    label: 'Please select',
    value: '',
    displayValue: 'Please select',
    disabled: true
  };

  const {
    isLoading,
    buyerProducts,
    content,
    approvedBuyersList,
    companyDetailsBySearchTerm,
    companyRegistrationDetails,
    pricingPolicies,
    csmTenants,
    cartProductDetails,
    user,
    checkUserAgreement,
    userAcceptedConsent,
    userConsentAgreement
  } = useSelector(
    ({
      ProductSearchServiceReducer,
      ContentReducer,
      UserServiceReducer,
      OrderSummaryReducer,
      OrchestrationReducer,
      loginReducer
    }) => ({
      isLoading: OrderSummaryReducer?.isLoading,
      buyerProducts: ProductSearchServiceReducer?.buyerProducts,
      content: ContentReducer?.content,
      approvedBuyersList: UserServiceReducer?.approvedBuyersList,
      companyDetailsBySearchTerm: UserServiceReducer?.companyDetailsBySearchTerm,
      companyRegistrationDetails: UserServiceReducer?.companyRegistrationDetails,
      pricingPolicies: OrderSummaryReducer?.pricingPolicies,
      csmTenants: UserServiceReducer?.csmTenants,
      cartProductDetails: OrchestrationReducer?.cartProductDetails,
      user: loginReducer?.user,
      checkUserAgreement: loginReducer?.checkUserAgreement,
      userAcceptedConsent: loginReducer?.userAcceptedConsent,
      userConsentAgreement: loginReducer?.userConsentAgreement
    })
  );

  // Filter products based on buyer's country
  const filteredBuyerProducts = useMemo(() => {
    if (!buyerProducts?.length) return [];

    const buyerCountry = companyRegistrationDetails?.countryCode || REGION.IN.CODE;

    return buyerProducts.filter((product) => {
      const productUsage =
        product?.productDetailedInfo?.productDetail?.genericInformation?.productUsage
          ?.value;

      // If product usage is not defined, include the product
      if (!productUsage) {
        return true;
      }

      if (buyerCountry === REGION.IN.CODE) {
        return productUsage === PRODUCT_USAGE.DOMESTIC;
      } else {
        return productUsage === PRODUCT_USAGE.INTERNATIONAL;
      }
    });
  }, [buyerProducts, companyRegistrationDetails?.countryCode]);

  // Helper function to get default currency based on region
  const getDefaultCurrencyByRegion = useCallback(() => {
    const regionCode = REGION_CODE?.toUpperCase();

    switch (regionCode) {
      case 'APAC':
        return 'INR';
      case 'EU':
        return 'EUR';
      case 'NA':
        return 'USD';
      default:
        return 'INR';
    }
  }, []);

  // Track current currency value from companyRegistrationDetails or default based on region
  const currentCurrency = useMemo(() => {
    return companyRegistrationDetails?.currencyCode || getDefaultCurrencyByRegion();
  }, [companyRegistrationDetails?.currencyCode, getDefaultCurrencyByRegion]);

  // Further filter products based on selected currency
  const currencyFilteredProducts = useMemo(() => {
    if (!filteredBuyerProducts?.length) return [];

    // Get the current currency code (e.g., 'INR', 'USD', 'EUR')
    const selectedCurrency = currentCurrency;

    return filteredBuyerProducts.filter((product) => {
      const currencyPreferences =
        product?.productDetailedInfo?.productDetail?.genericInformation
          ?.currencyPreferences;

      // If no currency preferences defined, include the product
      if (!currencyPreferences || !Array.isArray(currencyPreferences)) {
        return true;
      }

      // Check if the selected currency is in the product's currency preferences
      return currencyPreferences.some((currencyPref) => {
        const currencyValue = currencyPref?.value || '';
        return currencyValue === selectedCurrency;
      });
    });
  }, [filteredBuyerProducts, currentCurrency]);

  // Check if quantity field is required based on category (automotive software requires quantity)
  const isAXINCategory =
    currencyFilteredProducts?.[0]?.productCategory?.categoryId ===
    idConstants.automotiveSoftware.categoryId;

  // Handler to delete a product from the added products list
  const handleDeleteProduct = useCallback(
    (productId, planId) => {
      // Find the product to delete to get skuId
      const productToDelete = addedProducts.find(
        (product) => product.productId === productId && product.planId === planId
      );

      if (productToDelete) {
        // Prepare the update cart product payload with quantity 0 to delete
        const deleteCartPayload = {
          billingType: getBillingTypeCodeFromPlanType(productToDelete?.planDetails),
          planId: planId,
          productId: productId,
          quantity: 0,
          skuId: productToDelete.skuVariantId,
          buyerUserId: companyRegistrationDetails?.userId
        };

        // Call updateCartProduct API with quantity 0 to delete the item
        dispatch(updateCartProduct(deleteCartPayload)).then(() => {
          // Remove from local state after successful API call
          setAddedProducts((prev) =>
            prev.filter(
              (product) =>
                !(product.productId === productId && product.planId === planId)
            )
          );
        });
      }
    },
    [addedProducts, dispatch]
  );

  // Helper function to check if payment type is Postpaid
  const isPostpaidPlan = useCallback(() => {
    // Find the payment details section
    const paymentDetailsSection = placeOrderForm.find(
      (section) => section.id === 'payment-details'
    );

    if (!paymentDetailsSection) return false;

    // Find the paymentType field
    const paymentTypeField = paymentDetailsSection.fields.find(
      (field) => field.name === 'paymentType'
    );

    if (!paymentTypeField?.value) return false;

    // Check if the selected payment type is POSTPAID
    return paymentTypeField.value === idConstants.pricingPaymentType.postpaid;
  }, [placeOrderForm]);

  const currentPaymentType = useMemo(() => {
    const paymentDetailsSection = placeOrderForm.find(
      (section) => section.id === 'payment-details'
    );
    const paymentTypeField = paymentDetailsSection?.fields.find(
      (field) => field.name === 'paymentType'
    );
    return paymentTypeField?.value;
  }, [placeOrderForm]);

  const isPostpaidPaymentType =
    currentPaymentType === idConstants.pricingPaymentType.postpaid;

  // Memoized bulk order table configuration
  const bulkOrderTableConfig = useMemo(() => {
    const productTableColumnHeadings =
      content?.components?.place_order?.form?.product_order_table?.headings;

    const bulkOrderColDefs = [
      {
        field: 'productName',
        headerName: productTableColumnHeadings?.product_name,
        minWidth: 200,
        cellRenderer: CustomCell
      },
      {
        field: 'productDescription',
        headerName: productTableColumnHeadings?.description,
        width: 200,
        cellRenderer: CustomCell
      },
      // Conditionally include additional information column for AXIN category and prepaid payment type
      ...(isAXINCategory && !isPostpaidPaymentType
        ? [
            {
              field: 'additionalInformation',
              headerName: productTableColumnHeadings?.additional_information,
              minWidth: 250,
              flex: 2,
              cellRenderer: AdditionalInfoCellRenderer,
              autoHeight: true,
              wrapText: true
            }
          ]
        : []),
      {
        field: 'price',
        headerName: currentCurrency
          ? `${productTableColumnHeadings?.price} (${currentCurrency})`
          : productTableColumnHeadings?.price,
        minWidth: 150,
        flex: 1
      },
      // Conditionally include quantity column only when quantity is required
      ...(isAXINCategory
        ? [
            {
              field: 'quantity',
              headerName: productTableColumnHeadings?.quantity,
              minWidth: 100,
              flex: 0.8
            }
          ]
        : []),
      // Conditionally include tax column only for IN country and when company is selected
      ...(companyRegistrationDetails?.countryCode &&
      companyRegistrationDetails.countryCode === REGION.IN.CODE
        ? [
            {
              field: 'tax',
              headerName: productTableColumnHeadings?.tax,
              headerTooltip: productTableColumnHeadings?.tax,
              minWidth: 100,
              flex: 1
            }
          ]
        : []),
      {
        field: 'totalAmount',
        headerName: currentCurrency
          ? `${productTableColumnHeadings?.total_amount} (${currentCurrency})`
          : productTableColumnHeadings?.total_amount,
        headerTooltip: currentCurrency
          ? `${productTableColumnHeadings?.total_amount} (${currentCurrency})`
          : productTableColumnHeadings?.total_amount,
        minWidth: 150,
        flex: 1
      },
      {
        field: 'actions',
        headerName: 'Actions',
        minWidth: 100,
        flex: 0.5,
        cellRenderer: ActionsCellRenderer,
        cellRendererParams: {
          onDelete: handleDeleteProduct
        }
      }
    ];

    const gridProps = {
      rowData: addedProducts,
      columnDefs: bulkOrderColDefs,
      suppressDragLeaveHidesColumns: true,
      suppressMoveWhenColumnDragging: true,
      defaultColDef: {
        cellClass: 'center-align-cell',
        headerClass: 'center-align-cell'
      }
    };

    return {
      gridProps
    };
  }, [
    addedProducts,
    content,
    handleDeleteProduct,
    isAXINCategory,
    currentCurrency,
    companyRegistrationDetails,
    placeOrderForm,
    isPostpaidPaymentType
  ]);

  // Memoized field mappings with dynamic currency
  const fieldMappings = useMemo(() => {
    const formLabels = content?.components?.place_order?.form;

    return {
      paymentType: formLabels?.payment_details?.payment_type,
      companyName: formLabels?.customer_details?.company_name,
      customerName: formLabels?.customer_details?.customer_name,
      email: formLabels?.customer_details?.email,
      gst: formLabels?.customer_details?.gst,
      phoneNumber: formLabels?.customer_details?.phone_number,
      productName: formLabels?.product_details?.product_name,
      price: formLabels?.product_details?.price,
      quantity: formLabels?.product_details?.quantity,
      additionalInformation: formLabels?.product_details?.additional_information,
      addProduct: formLabels?.product_details?.add_product
    };
  }, [content]);

  // Helper function to update form sections
  const updateFormSections = useCallback(
    (updateForm) => {
      setPlaceOrderForm((prev) =>
        prev.map((section) => ({
          ...section,
          fields: section.fields.map(updateForm)
        }))
      );
    },
    [setPlaceOrderForm]
  );

  const updatePriceFields = useCallback(
    (priceValue, shouldDisable = false) => {
      const price = parseInt(priceValue);
      if (isNaN(price)) return;

      updateFormSections((field) => {
        switch (field.name) {
          case FieldConstants.price:
            // If price is 0, set as empty string and keep field editable; otherwise follow shouldDisable
            return {
              ...field,
              value: price === 0 ? '' : price?.toString(),
              disabled: price === 0 ? false : shouldDisable
            };
          default:
            return field;
        }
      });
    },
    [updateFormSections]
  );

  const updateQuantityFields = useCallback(
    (quantityValue) => {
      updateFormSections((field) => {
        switch (field.name) {
          case FieldConstants.quantity:
            return {
              ...field,
              value: quantityValue?.toString()
            };
          default:
            return field;
        }
      });
    },
    [updateFormSections]
  );

  // Helper function to get customer details from registration data
  const getCustomerFieldValue = useCallback((fieldName, customerData) => {
    const fieldValueMap = {
      [FieldConstants.customerName]:
        customerData?.userFirstName && customerData?.userLastName
          ? `${customerData.userFirstName} ${customerData.userLastName}`
          : '',
      [FieldConstants.email]: customerData?.userEmailId || '',
      [FieldConstants.phoneNumber]: customerData?.userTelephoneNo || '',
      [FieldConstants.gst]:
        customerData?.compAttributes?.find(
          (attr) => attr.attributeName.toLowerCase() === FieldConstants.gst
        )?.value || ''
    };
    return fieldValueMap[fieldName] || '';
  }, []);

  const consentAcceptancePayload = {
    data: {
      docMetaInfo: [
        {
          docMetaInfoId: idConstants.docMetaInfoId.dpnDocumentMetaInfoId,
          documentTypeId: idConstants.agreementTypes.privacyPolicies,
          userConsentNeeded: true,
          accepted: true
        }
      ],
      retrieveCompanyDetails: false
    },
    metaInfo: {
      typeOfUser: idConstants.typeOfUsers.b2b.toUpperCase(),
      userId: companyRegistrationDetails?.userId,
      role: accessRoles.csm
    }
  };

  // Function to update price fields (price, tax, total)

  // Helper function to populate customer details in form
  const populateCustomerDetails = useCallback(() => {
    if (
      selectedCustomerId &&
      companyRegistrationDetails.companyId === selectedCustomerId
    ) {
      updateFormSections((field) => {
        const buyerCountry =
          companyRegistrationDetails?.countryCode || REGION.IN.CODE;
        const customerFields = [
          FieldConstants.customerName,
          FieldConstants.email,
          FieldConstants.phoneNumber,
          ...(buyerCountry === REGION.IN.CODE ? [FieldConstants.gst] : [])
        ];
        if (customerFields.includes(field.name)) {
          return {
            ...field,
            value: getCustomerFieldValue(field.name, companyRegistrationDetails)
          };
        }
        return field;
      });
    }
  }, [companyRegistrationDetails, selectedCustomerId]);

  const selectCustomer = useCallback((customerId) => {
    if (customerId && customerId !== lastCustomerApiCallRef.current) {
      lastCustomerApiCallRef.current = customerId;
      setSelectedCustomerId(customerId);
      dispatch(getCompanyRegistrationDetails(AAA_CONFIG.BUYER_ROLE, customerId));
    }
  }, []);

  // Helper function to call pricing API only once per product
  const callPricingAPI = useCallback(
    (productId) => {
      if (productId && productId !== lastPricingApiCallRef.current) {
        lastPricingApiCallRef.current = productId;
        dispatch(getPricingPolicies(productId));
        dispatch(getCartProductDetails([productId]));
      }
    },
    [dispatch]
  );

  // Helper function to restore approved buyers list
  const restoreApprovedBuyersList = useCallback(() => {
    if (approvedBuyersList?.length) {
      const initialCompaniesList = [
        PLACEHOLDER_OPTION,
        ...approvedBuyersList.map((company) => ({
          id: company.companyId,
          label: company.companyName,
          value: company.companyName,
          displayValue: company.companyName
        }))
      ];

      updateFormSections((fieldToUpdate) => {
        if (fieldToUpdate.name === FieldConstants.companyName) {
          return {
            ...fieldToUpdate,
            options: initialCompaniesList,
            // Preserve free-text while typing; only set placeholder if truly empty
            value:
              typeof fieldToUpdate.value === 'string' &&
              fieldToUpdate.value.length > 0
                ? fieldToUpdate.value
                : fieldToUpdate.value?.id
                  ? fieldToUpdate.value
                  : PLACEHOLDER_OPTION
          };
        }
        return fieldToUpdate;
      });
    }
  }, [approvedBuyersList, updateFormSections]);

  const handleFieldChange = useCallback(
    (field, name, value) => {
      // Handle company selection from dropdown - call registration details API
      if (name === FieldConstants.companyName && value?.id) {
        selectCustomer(value.id);
      }

      if (field.name === name) return { ...field, value };

      if (name === FieldConstants.productName) {
        // Find the selected product and call pricing API
        const selectedProduct = currencyFilteredProducts?.find(
          (product) => product?.productName === value?.displayValue
        );
        if (selectedProduct?.productId) {
          callPricingAPI(selectedProduct.productId);
        }
      }

      if (
        name === FieldConstants.productName &&
        value?.id &&
        lastProductApiCallRef.current !== value?.id &&
        field.name === FieldConstants.shortDescription
      ) {
        lastProductApiCallRef.current = value?.id;
        const selectedProduct = currencyFilteredProducts?.find(
          (product) => product?.productName === value?.displayValue
        );
        return { ...field, value: selectedProduct?.productShortDesc || '' };
      }

      return field;
    },
    [selectCustomer, currencyFilteredProducts, updatePriceFields]
  );

  useEffect(() => {
    if (csmTenants?.tenantId) {
      let category = '';
      switch (csmTenants?.tenantId) {
        case 'main':
          category = idConstants.afterMarketServices.categoryId;
          break;
        case 'axin':
          category = idConstants.automotiveSoftware.categoryId;
          break;
        default:
          category = idConstants.afterMarketServices.categoryId;
      }
      // First call with groupBy to get counts
      dispatch(getBuyerProducts(category, 'categories')).then((response) => {
        if (response?.counts) {
          const countValue = Object.values(response.counts)[0];
          if (countValue) {
            dispatch(getBuyerProducts(category, null, countValue));
          }
        }
      });
      dispatch(
        getApprovedBuyersList(500, idConstants.approved.description.toLowerCase())
      );
      dispatch(checkUserAgreementAcceptance());
    }
  }, [csmTenants?.tenantId]);

  // Effect for setting up initial company details from approved buyers list
  useEffect(() => {
    if (approvedBuyersList?.length) {
      restoreApprovedBuyersList();
    }
  }, [approvedBuyersList?.length]);

  // Load initial cart products on component mount
  useEffect(() => {
    if (companyRegistrationDetails?.userId && csmTenants?.tenantId) {
      dispatch(getCartProduct(companyRegistrationDetails?.userId)).then(
        async (cartResponse) => {
          if (cartResponse && cartResponse.length > 0) {
            const cart = cartResponse[0];
            setCartId(cart?.cartId);

            // Fetch pricing policies for all products at once
            const uniqueProductIds = [
              ...new Set(cart?.items?.map((item) => item.productId))
            ];

            if (uniqueProductIds.length === 0) return;

            // Fetch cart product details for all cart items
            const cartProductDetailsData = await dispatch(
              getCartProductDetails(uniqueProductIds)
            );

            // Call the new API with the list of product IDs
            const payload = uniqueProductIds.map((productId) => ({
              itemId: productId
            }));

            const allPricingPolicies = await dispatch(
              getPricingDetailsByProductIdList(payload)
            );

            // Build cart products with pricing data
            const cartProducts = cart?.items
              ?.map((item) => {
                // Use the response data directly to avoid timing issues
                const product = cartProductDetailsData.find(
                  (p) => p.productId === item.productId
                );
                const plan = allPricingPolicies.find(
                  (p) => p.planId === item.planId
                );

                if (!product || !plan) return null;

                const price = plan?.planCost || 0;
                const quantity = item.quantity || 1;
                // Apply 18% tax for IN country, 0% for others
                const buyerCountry =
                  companyRegistrationDetails?.countryCode || REGION.IN.CODE;
                const tax =
                  buyerCountry === REGION.IN.CODE
                    ? parseFloat((price * quantity * 0.18).toFixed(2))
                    : 0;
                const totalAmount = parseFloat((price * quantity + tax).toFixed(2));

                return {
                  productId: item.productId,
                  productName: product.productName,
                  productDescription: product.productShortDesc,
                  planId: item.planId,
                  itemId: plan?.itemId,
                  categoryId: plan?.categoryId,
                  skuVariantId: item.skuId,
                  price: price * quantity,
                  quantity: quantity,
                  tax: tax,
                  totalAmount: totalAmount,
                  createdBy: product?.createdBy,
                  companyNo: product?.companyNo,
                  planDetails: plan?.planDetails
                };
              })
              .filter(Boolean);

            if (cartProducts.length > 0) {
              setAddedProducts(cartProducts);
            }
          }
        }
      );
    }
  }, [companyRegistrationDetails?.userId, csmTenants?.tenantId, dispatch]);

  // Initialize form with placeOrderFormData on component mount
  useEffect(() => {
    if (placeOrderForm.length === 0 && csmTenants?.tenantId) {
      const initialForm = placeOrderFormData
        .filter((section) => {
          // Only show payment-details section for axin tenant
          if (section.id === 'payment-details') {
            return csmTenants?.tenantId === 'axin';
          }
          return true;
        })
        .map((section) => ({
          ...section,
          fields: section.fields.map((field) => {
            const baseFieldConfig = {
              ...field,
              displayName: fieldMappings[field.name] || field.displayName,
              label: fieldMappings[field.name] || field.label,
              // Hide quantity and additional information fields based on category and payment type
              // Hide GST field by default (will be shown only for Indian buyers)
              hidden:
                (field.name === FieldConstants.quantity && !isAXINCategory) ||
                (field.name === FieldConstants.additionalInformation &&
                  (!isAXINCategory || isPostpaidPaymentType)) ||
                field.name === FieldConstants.gst,
              // Disable fields based on whether company is selected
              disabled:
                field.name === FieldConstants.price ||
                field.name === FieldConstants.quantity ||
                field.name === FieldConstants.productName ||
                field.name === 'paymentType'
                  ? true // Disable until company/product is selected
                  : field.disabled // Preserve original disabled state from form data
            };

            return baseFieldConfig;
          })
        }));
      setPlaceOrderForm(initialForm);
    }
  }, [
    placeOrderForm.length,
    fieldMappings,
    isAXINCategory,
    setPlaceOrderForm,
    csmTenants?.tenantId
  ]);
  // Update form labels when fieldMappings change (without reinitializing)
  useEffect(() => {
    if (placeOrderForm.length > 0 && Object.keys(fieldMappings).length > 0) {
      updateFormSections((field) => {
        // Skip price field as it's handled by the currency effect
        if (field.name === FieldConstants.price) {
          return field;
        }
        // Skip GST field as it's handled by the country-based effect
        if (field.name === FieldConstants.gst) {
          return {
            ...field,
            displayName: fieldMappings[field.name] || field.displayName,
            label: fieldMappings[field.name] || field.label
          };
        }
        return {
          ...field,
          displayName: fieldMappings[field.name] || field.displayName,
          label: fieldMappings[field.name] || field.label,
          // Hide quantity and additional information fields based on category and payment type
          hidden:
            (field.name === FieldConstants.quantity && !isAXINCategory) ||
            (field.name === FieldConstants.additionalInformation &&
              (!isAXINCategory || isPostpaidPaymentType))
        };
      });
    }
  }, [
    fieldMappings,
    updateFormSections,
    isAXINCategory,
    placeOrderForm.length,
    isPostpaidPaymentType
  ]);

  // Update price field label when currency changes
  useEffect(() => {
    if (placeOrderForm.length > 0) {
      const formLabels = content?.components?.place_order?.form;
      const basePriceLabel = formLabels?.product_details?.price;

      if (basePriceLabel) {
        setPlaceOrderForm((prev) =>
          prev.map((section) => ({
            ...section,
            fields: section.fields.map((field) => {
              if (field.name === FieldConstants.price) {
                return {
                  ...field,
                  displayName: currentCurrency
                    ? `${basePriceLabel} (${currentCurrency})`
                    : basePriceLabel,
                  label: currentCurrency
                    ? `${basePriceLabel} (${currentCurrency})`
                    : basePriceLabel
                };
              }
              return field;
            })
          }))
        );
      }
    }
  }, [currentCurrency, content, setPlaceOrderForm, placeOrderForm.length]);

  // Hide/show GST field based on buyer country
  useEffect(() => {
    if (placeOrderForm.length > 0) {
      const buyerCountry = companyRegistrationDetails?.countryCode;
      // Only show GST if company is selected AND country is IN
      const shouldShowGst = buyerCountry && buyerCountry === REGION.IN.CODE;

      setPlaceOrderForm((prev) =>
        prev.map((section) => ({
          ...section,
          fields: section.fields.map((field) => {
            if (field.name === FieldConstants.gst) {
              return { ...field, hidden: !shouldShowGst };
            }
            return field;
          })
        }))
      );
    }
  }, [
    companyRegistrationDetails?.countryCode,
    placeOrderForm.length,
    setPlaceOrderForm
  ]);

  // Effect for setting up company details from search
  useEffect(() => {
    if (companyDetailsBySearchTerm?.length) {
      const searchCompaniesList = [
        PLACEHOLDER_OPTION,
        ...companyDetailsBySearchTerm.map((company) => ({
          id: company.companyId,
          label: company.companyName,
          value: company.companyName,
          displayValue: company.companyName
        }))
      ];

      updateFormSections((field) => {
        if (field.name === FieldConstants.companyName) {
          return {
            ...field,
            options: searchCompaniesList,
            // Preserve free-text while typing; only set placeholder if truly empty
            value:
              typeof field.value === 'string' && field.value.length > 0
                ? field.value
                : field.value?.id
                  ? field.value
                  : PLACEHOLDER_OPTION
          };
        }
        return field;
      });
    }
  }, [companyDetailsBySearchTerm, updateFormSections]);

  // Debug removed: avoid console noise in production

  // Memoize filtered product list
  const availableProducts = useMemo(() => {
    if (!currencyFilteredProducts?.length) return [];

    const productOptions = currencyFilteredProducts.map(
      ({ productId, productName, productShortDesc }) => ({
        id: productId,
        productId: productId,
        label: productName,
        value: productName,
        displayValue: productName,
        productShortDesc
      })
    );

    return [PLACEHOLDER_OPTION, ...productOptions];
  }, [currencyFilteredProducts]);

  useEffect(() => {
    if (availableProducts.length > 0) {
      updateFormSections((field) => {
        if (field.name === FieldConstants.productName) {
          const optionsChanged =
            !Array.isArray(field.options) ||
            field.options.length !== availableProducts.length;
          const shouldResetValue = !field.value || !field.value?.id;
          if (optionsChanged || shouldResetValue) {
            return {
              ...field,
              options: availableProducts,
              value: availableProducts[0]
            };
          }
        }
        return field;
      });
    }
  }, [availableProducts]);

  // Memoize available plans
  const availablePlans = useMemo(() => {
    if (!pricingPolicies?.length) return [];

    return pricingPolicies?.map((plan) => ({
      id: plan.planId,
      planId: plan.planId,
      label: plan?.planName,
      value: plan?.planName,
      displayValue: plan?.planName,
      planCost: plan?.planCost
    }));
  }, [pricingPolicies, addedProducts]);

  // Effect to auto-fill price and plan name options when pricing policies are received
  useEffect(() => {
    if (availablePlans.length > 0) {
      updateFormSections((field) => {
        if (field.name === FieldConstants.price) {
          return {
            ...field,
            disabled: false // Enable price field when product is selected
          };
        }
        if (field.name === FieldConstants.quantity) {
          return {
            ...field,
            disabled: false // Enable quantity field when product is selected
          };
        }
        return field;
      });

      // Auto-fill price for the first/default plan
      const priceValue = availablePlans[0]?.planCost;
      if (priceValue !== undefined && !isNaN(parseInt(priceValue))) {
        updatePriceFields(priceValue, true);
      }
    } else {
      // Disable fields when no plans are available (no product selected)
      updateFormSections((field) => {
        if (field.name === FieldConstants.price) {
          return {
            ...field,
            value: '',
            disabled: true // Disable price field when no product is selected
          };
        }
        if (field.name === FieldConstants.quantity) {
          return {
            ...field,
            value: '',
            disabled: true // Disable quantity field when no product is selected
          };
        }
        return field;
      });
    }
  }, [availablePlans, updateFormSections, updatePriceFields]);

  // Check if Add Product button should be disabled
  const isAddProductDisabled = useMemo(() => {
    const customerDetailsSection = placeOrderForm.find(
      (section) => section.id === 'customer-details'
    );
    const productDetailsSection = placeOrderForm.find(
      (section) => section.id === 'product-details'
    );

    if (!productDetailsSection) return true;

    // Check if company name is selected
    const companyNameField = customerDetailsSection?.fields.find(
      (f) => f.name === FieldConstants.companyName
    );
    const isCompanySelected = companyNameField?.value?.id != null;

    const productNameField = productDetailsSection.fields.find(
      (f) => f.name === FieldConstants.productName
    );
    const priceField = productDetailsSection.fields.find(
      (f) => f.name === FieldConstants.price
    );
    const quantityField = productDetailsSection.fields.find(
      (f) => f.name === FieldConstants.quantity
    );

    // Parse numeric values
    const priceValue = parseInt(priceField?.value);
    const quantityValue = parseInt(quantityField?.value);

    // Check if company is not selected
    if (!isCompanySelected) {
      return true;
    }

    // Check if any required field is empty or not greater than 0
    let hasEmptyFields =
      !productNameField?.value ||
      !productNameField?.value?.id ||
      !priceField?.value ||
      isNaN(priceValue) ||
      priceValue <= 0;

    // Only check quantity if it's required (for automotive software category)
    if (isAXINCategory) {
      hasEmptyFields =
        hasEmptyFields ||
        !quantityField?.value ||
        isNaN(quantityValue) ||
        quantityValue <= 0;
    }

    // Check if any field has an error
    const hasErrors = !!priceField?.error || !!quantityField?.error;

    return hasEmptyFields || hasErrors;
  }, [placeOrderForm, isAXINCategory]);

  // Compute form sections with the Add Product button disabled state applied
  const formSectionsWithButtonState = useMemo(() => {
    return placeOrderForm.map((section) => ({
      ...section,
      fields: section.fields
        .filter((field) => !field.hidden) // Filter out hidden fields
        .map((field) => {
          if (field.name === FieldConstants.addProduct) {
            return {
              ...field,
              disabled: isAddProductDisabled
            };
          }
          return field;
        })
    }));
  }, [placeOrderForm, isAddProductDisabled]);

  useEffect(() => {
    if (
      userConsentAgreement &&
      Array.isArray(userConsentAgreement) &&
      userConsentAgreement?.length === 0
    ) {
      setShowConsent(true);
    } else if (userConsentAgreement?.[0]?.accepted) {
      setShowConsent(false);
      localStorage.setItem(
        idConstants.consentAccepted,
        userConsentAgreement[0]?.accepted
      );
    }
  }, [userConsentAgreement]);

  useEffect(() => {
    if (companyRegistrationDetails?.companyId === selectedCustomerId) {
      populateCustomerDetails();
    }
  }, [companyRegistrationDetails, selectedCustomerId]);

  // Effect to enable/disable Product name and Payment type fields based on company selection
  useEffect(() => {
    if (placeOrderForm.length > 0) {
      const isCompanySelected = selectedCustomerId !== null;

      updateFormSections((field) => {
        if (
          field.name === FieldConstants.productName ||
          field.name === 'paymentType'
        ) {
          return {
            ...field,
            disabled: !isCompanySelected
          };
        }
        return field;
      });
    }
  }, [selectedCustomerId, placeOrderForm.length, updateFormSections]);

  // Effect to disable payment type field for international buyers and set to Postpaid
  useEffect(() => {
    if (placeOrderForm.length > 0 && companyRegistrationDetails?.countryCode) {
      const buyerCountry = companyRegistrationDetails?.countryCode;
      const isInternationalBuyer = buyerCountry !== REGION.IN.CODE;

      updateFormSections((field) => {
        if (field.name === 'paymentType') {
          const paymentTypeOptions = [
            {
              name: 'Prepaid',
              value: idConstants.pricingPaymentType.prepaid,
              selected: !isInternationalBuyer
            },
            {
              name: 'Postpaid',
              value: idConstants.pricingPaymentType.postpaid,
              selected: isInternationalBuyer
            }
          ];

          return {
            ...field,
            disabled: isInternationalBuyer,
            options: paymentTypeOptions,
            value: isInternationalBuyer
              ? idConstants.pricingPaymentType.postpaid
              : idConstants.pricingPaymentType.prepaid
          };
        }
        // Clear additional information field when it should be hidden (postpaid)
        if (
          field.name === FieldConstants.additionalInformation &&
          isInternationalBuyer
        ) {
          return {
            ...field,
            value: ''
          };
        }
        return field;
      });
    }
  }, [
    companyRegistrationDetails?.countryCode,
    placeOrderForm.length,
    updateFormSections
  ]);

  // Effect to delete cart and clear products when selected customer changes
  useEffect(() => {
    if (selectedCustomerId && cartId && companyRegistrationDetails?.userId) {
      // Check if we're changing to a different customer (not initial load)
      if (companyRegistrationDetails?.companyId !== selectedCustomerId) {
        // Only delete cart if there are products in the cart
        if (addedProducts.length > 0) {
          // Check if this cart was already deleted
          const cartKey = `${cartId}-${companyRegistrationDetails.userId}`;
          if (!deletedCartIdsRef.current.has(cartKey)) {
            // Delete the old customer's cart
            dispatch(deleteCartProducts(cartId, companyRegistrationDetails.userId));
            deletedCartIdsRef.current.add(cartKey);
          }
        }
        setCartId(null);
        setAddedProducts([]);
      }
    }
  }, [selectedCustomerId]);

  // Effect to delete cart products when Payment type changes
  useEffect(() => {
    // Skip if no cart or user
    if (!cartId || !companyRegistrationDetails?.userId) {
      return;
    }

    // Do not run cart deletion logic if last changed field was Add Product
    if (lastChangedFieldRef.current === FieldConstants.addProduct) {
      return;
    }

    // Get the payment type value (handle both object and primitive values)
    const currentPaymentTypeValue =
      typeof currentPaymentType === 'object'
        ? currentPaymentType?.value
        : currentPaymentType;

    // Check if values have changed (after refs have been initialized)
    const paymentTypeChanged =
      prevPaymentTypeRef.current !== undefined &&
      prevPaymentTypeRef.current !== currentPaymentTypeValue;

    // If payment type changed, delete cart and clear products
    if (paymentTypeChanged) {
      // Check if this cart was already deleted
      const cartKey = `${cartId}-${companyRegistrationDetails.userId}`;
      if (!deletedCartIdsRef.current.has(cartKey)) {
        dispatch(deleteCartProducts(cartId, companyRegistrationDetails.userId));
        deletedCartIdsRef.current.add(cartKey);
        setCartId(null);
        setAddedProducts([]);

        // Clear additional information field when switching to postpaid
        if (currentPaymentTypeValue === idConstants.pricingPaymentType.postpaid) {
          updateFormSections((field) => {
            if (field.name === FieldConstants.additionalInformation) {
              return { ...field, value: '' };
            }
            return field;
          });
        }
      }
    }

    // Update refs with current values (use undefined initially to distinguish from null values)
    if (prevPaymentTypeRef.current === undefined) {
      prevPaymentTypeRef.current = currentPaymentTypeValue;
    } else if (paymentTypeChanged) {
      prevPaymentTypeRef.current = currentPaymentTypeValue;
    }
  }, [
    currentPaymentType,
    cartId,
    companyRegistrationDetails?.userId,
    dispatch,
    updateFormSections
  ]);

  useEffect(() => {
    const dpnLink = checkUserAgreement?.find(
      (doc) => doc?.documentTypeId === idConstants.agreementTypes.privacyPolicies
    )?.termsAndCondUrl;
    if (dpnLink && showConsent) {
      localStorage.setItem(
        idConstants.dpnLink,
        checkUserAgreement?.find(
          (doc) => doc?.documentTypeId === idConstants.agreementTypes.privacyPolicies
        )?.termsAndCondUrl
      );
    }
  }, [checkUserAgreement]);

  // Cleanup effect - clear selections on component unmount
  useEffect(() => {
    return () => {
      // Restore approved buyers list before clearing
      if (approvedBuyersList?.length) {
        const initialCompaniesList = [
          PLACEHOLDER_OPTION,
          ...approvedBuyersList.map((company) => ({
            id: company.companyId,
            label: company.companyName,
            value: company.companyName,
            displayValue: company.companyName
          }))
        ];

        setPlaceOrderForm((prev) =>
          prev.map((section) => ({
            ...section,
            fields: section.fields.map((field) => {
              // Restore company name options
              if (field.name === FieldConstants.companyName) {
                return {
                  ...field,
                  options: initialCompaniesList,
                  value: PLACEHOLDER_OPTION
                };
              }
              // Clear other customer detail fields
              if (
                field.name === FieldConstants.customerName ||
                field.name === FieldConstants.email ||
                field.name === FieldConstants.phoneNumber ||
                field.name === FieldConstants.gst ||
                field.name === FieldConstants.productName
              ) {
                return { ...field, value: null };
              }
              // Reset payment type to Prepaid on unmount
              if (field.name === 'paymentType') {
                return { ...field, value: idConstants.pricingPaymentType.prepaid };
              }
              return field;
            })
          }))
        );
      }

      // Clear selected customer
      setSelectedCustomerId(null);
      lastCustomerApiCallRef.current = null;

      // Clear selected product
      lastProductApiCallRef.current = null;
      lastPricingApiCallRef.current = null;

      // Clear added products
      setAddedProducts([]);

      dispatch({
        type: 'COMPANY_REGISTRATION_DETAILS',
        payload: null
      });

      // Clear cart ID
      setCartId(null);
    };
  }, [approvedBuyersList]);

  // Keep refs updated with latest values
  useEffect(() => {
    cartIdRef.current = cartId;
    userIdRef.current = companyRegistrationDetails?.userId;
  }, [cartId, companyRegistrationDetails?.userId]);

  // Delete cart products on component unmount only
  useEffect(() => {
    return () => {
      const currentCartId = cartIdRef.current;
      const currentUserId = userIdRef.current;

      if (currentCartId && currentUserId) {
        const cartKey = `${currentCartId}-${currentUserId}`;
        if (!deletedCartIdsRef.current.has(cartKey)) {
          dispatch(deleteCartProducts(currentCartId, currentUserId));
          deletedCartIdsRef.current.add(cartKey);
        }
      }
    };
  }, [dispatch]);

  const orderRequestPayloads = useMemo(() => {
    const userDetails = {
      typeOfUser: idConstants.typeOfUsers.b2b.toUpperCase(),
      b2bIdentifier: companyRegistrationDetails?.companyId,
      userId: companyRegistrationDetails?.userId,
      emailId: companyRegistrationDetails?.userEmailId,
      role: AAA_CONFIG.BUYER_ROLE,
      bpNumber: null
    };

    // Map addedProducts to productOrderDetailDto format
    const productOrderDetails = addedProducts.map((product) => ({
      planId: product.planId,
      quantity: product.quantity,
      itemId: product.productId,
      categoryId: product.categoryId,
      skuVariant: product.skuVariantId,
      createdBy: product.createdBy,
      companyId: product.companyNo
    }));

    // Use first product's plan for payment details (assuming all same payment type)
    const firstProduct = addedProducts[0];
    // Use persisted planDetails from the added product to avoid relying on pricingPolicies state
    const firstPlanDetails = firstProduct?.planDetails;

    return {
      order: () => ({
        data: {
          productOrderDetailDto: productOrderDetails,
          checkoutId: null,
          cartId: cartId,
          paymentDetails: firstPlanDetails
        },
        userDetails: userDetails,
        mappingInfo: { mappedBy: null },
        classifiers: { classifiedBy: cartId ?? firstProduct?.itemId }
      }),

      checkout: (productOrderId) => ({
        data: {
          cartId: cartId,
          cartItems: addedProducts.map((product) => ({
            productVariantId: product.skuVariantId,
            quantity: product.quantity
          })),
          trackingParameters: [
            { key: idConstants.productOrderId, value: productOrderId }
          ],
          ...(cartId
            ? {
                itemId: firstProduct?.itemId,
                planId: firstProduct?.planId
              }
            : {})
        },
        userDetails: userDetails,
        mappingInfo: { mappedBy: productOrderId },
        classifiers: { classifiedBy: null }
      }),

      save: (productOrderId, checkoutId) => ({
        data: {
          productOrderId,
          checkoutId,
          productOrderDetailDto: productOrderDetails
        },
        userDetails: userDetails,
        paymentType: idConstants.paymentLink,
        mappingInfo: { mappedBy: productOrderId },
        classifiers: { classifiedBy: cartId ?? firstProduct?.itemId }
      }),

      address: (checkoutId) => ({
        buyerUserId: companyRegistrationDetails?.userId,
        checkoutId,
        dcsAddressId: companyRegistrationDetails?.billingAddress?.addressId
      }),

      process: (productOrderId, checkoutId) => {
        // Calculate total gross amount of all products including tax and quantity
        const grossAmount = parseFloat(
          addedProducts.reduce((sum, product) => sum + product.totalAmount, 0)
        ).toFixed(2);

        const currency =
          companyRegistrationDetails?.currencyCode || getDefaultCurrencyByRegion();

        return {
          data: {
            checkoutId: checkoutId,
            itemOrderId: productOrderId,
            paymentDetailsList: addedProducts.map((product) => ({
              itemId: product.itemId,
              unitPrice: String(product.price),
              amount: String(product.totalAmount),
              quantity: String(product.quantity),
              ...(isAXINCategory && {
                orderProductInfo: product.additionalInformation || ''
              })
            })),
            totalPrice: String(grossAmount),
            preferredCurrency: currency,
            paymentLinkOrder: !isPostpaidPlan(),
            postpaidOrder: isPostpaidPlan(),
            addressId: companyRegistrationDetails?.billingAddress?.addressId,
            categoryId: firstProduct?.categoryId
          },
          userDetails: userDetails,
          mappingInfo: { mappedBy: productOrderId },
          classifiers: { classifiedBy: null }
        };
      },

      // Build order subscription payload for Order Fulfillment service
      orderSubscription: (productOrderId, prepaidOrder) => ({
        tenantId: csmTenants?.tenantId,
        productOrderId: productOrderId,
        productInfo: addedProducts.map((product) => ({
          productId: product.itemId,
          policyId: product.planId
        })),
        status: 'ORDER_PLACED',
        csmOrder: true,
        prepaidOrder: prepaidOrder
      })
    };
  }, [
    companyRegistrationDetails,
    pricingPolicies,
    addedProducts,
    cartId,
    placeOrderForm,
    csmTenants?.tenantId
  ]);

  const updateFieldValue = useCallback(
    (formData, fieldName, newValue, error = '') => {
      return formData.map((section) => ({
        ...section,
        fields: section.fields.map((field) => {
          if (field.name === fieldName) {
            // Prevent setting NaN in quantity field; coerce to numeric string when valid
            if (fieldName === FieldConstants.quantity) {
              // Allow empty string to be set (user is clearing the field)
              if (newValue === '' || newValue === null || newValue === undefined) {
                return { ...field, value: '0', error };
              }

              return { ...field, value: newValue, error };
            }
            return { ...field, value: newValue, error };
          }
          return handleFieldChange(field, fieldName, newValue);
        })
      }));
    },
    [handleFieldChange]
  );

  // Debug removed: avoid console noise in production

  const handleFormChange = useCallback(
    ({ value, name, error, inputValue, changeType }) => {
      // Ignore spurious calls with no field context
      if (!name) return;

      const fieldValue = changeType === 'input' ? inputValue : value;

      const currentSection = placeOrderForm.find((section) =>
        section.fields.some((f) => f.name === name)
      );
      const currentField = currentSection?.fields.find((f) => f.name === name);

      if (name === FieldConstants.productName) {
        if (currentField?.value?.id === null) {
          setPlaceOrderForm((prev) => updateFieldValue(prev, name, inputValue));
        }
        if (changeType === 'input' || !value || !value?.id) {
          return;
        }
      }

      // Company name: when placeholder is selected and user types, clear the field and enable search
      if (
        (name === FieldConstants.companyName &&
          changeType === 'input' &&
          typeof inputValue === 'string') ||
        (name === FieldConstants.companyName &&
          changeType === 'dropdown' &&
          !value?.id)
      ) {
        // Normalize typed text to avoid undefined access when changeType is 'dropdown'
        const typedText = typeof inputValue === 'string' ? inputValue : '';
        // Convert field to free text when current selection is the placeholder
        if (currentField?.value?.id === null && typedText.length > 0) {
          setPlaceOrderForm((prev) => updateFieldValue(prev, name, typedText));
        }

        // Clear any pending timeouts
        if (searchDebounceRef.current) {
          clearTimeout(searchDebounceRef.current);
        }

        // If input has 2 or more characters, trigger search API
        if (typedText.length >= 2 && typedText !== 'Please select') {
          searchDebounceRef.current = setTimeout(() => {
            searchActiveRef.current = true;
            dispatch(getCompanyDetailsBySearchTerm(typedText));
          }, 500);
        }
        // If input is cleared, restore approved buyers list immediately
        else if (typedText.length === 0) {
          // Restore full buyers list and reset value to placeholder
          searchDebounceRef.current = setTimeout(() => {
            restoreApprovedBuyersList();
            searchActiveRef.current = false;
          }, 100);
        }
        return;
      }

      // Handle manual price changes (restrict to numbers using isNaN)
      if (name === FieldConstants.price) {
        // Allow empty string (erasing the field) - will default to 0
        if (fieldValue === '' || fieldValue === null || fieldValue === undefined) {
          setPlaceOrderForm((prev) => updateFieldValue(prev, name, '0', error));
          return;
        }

        const priceVal = parseInt(fieldValue || value);
        // Set to 0 when the input is not a number (like +, -, etc.)
        if (isNaN(priceVal)) {
          setPlaceOrderForm((prev) => updateFieldValue(prev, name, '0', error));
          updatePriceFields(0, false);
          return;
        }
        // Coerce to numeric string and update dependent fields
        setPlaceOrderForm((prev) =>
          updateFieldValue(prev, name, priceVal.toString(), error)
        );
        updatePriceFields(priceVal, false);
        return; // Exit early to prevent bypassing validation below
      }

      // Handle payment type changes
      if (name === 'paymentType') {
        setPlaceOrderForm((prev) =>
          prev.map((section) => ({
            ...section,
            fields: section.fields.map((field) => {
              if (field.name === name) {
                return { ...field, value };
              }
              return field;
            })
          }))
        );
        lastChangedFieldRef.current = name;
        return;
      }

      setPlaceOrderForm((prev) => updateFieldValue(prev, name, fieldValue));
      lastChangedFieldRef.current = name;
      // Handle Add Product button click
      if (name === FieldConstants.addProduct) {
        const productDetailsSection = placeOrderForm.find(
          (section) => section.id === 'product-details'
        );

        if (productDetailsSection) {
          const productNameField = productDetailsSection.fields.find(
            (f) => f.name === FieldConstants.productName
          );
          const quantityField = productDetailsSection.fields.find(
            (f) => f.name === FieldConstants.quantity
          );
          const priceField = productDetailsSection.fields.find(
            (f) => f.name === FieldConstants.price
          );
          const additionalInformationField = productDetailsSection.fields.find(
            (f) => f.name === FieldConstants.additionalInformation
          );

          // Use quantity from field or default to 1 when quantity is not required
          const quantity = !isAXINCategory ? 1 : quantityField?.value || 1;

          // Apply 18% tax for IN country, 0% for others
          const buyerCountry =
            companyRegistrationDetails?.countryCode || REGION.IN.CODE;
          const taxAmount =
            buyerCountry === REGION.IN.CODE
              ? parseFloat(
                  (parseFloat(priceField?.value) * quantity * 0.18).toFixed(2)
                )
              : 0;
          const totalAmount = parseFloat(
            (parseFloat(priceField?.value) * quantity + taxAmount).toFixed(2)
          );

          // Get productId from field value and use first available plan automatically
          const productId =
            typeof productNameField?.value === 'object'
              ? productNameField?.value?.productId || productNameField?.value?.id
              : null;

          // Find selected product using ID
          const selectedProduct = currencyFilteredProducts?.find(
            (product) => product?.productId === productId
          );

          const selectedPlan = pricingPolicies?.find(
            (policy) => policy.planId === availablePlans[0]?.planId
          );

          if (
            selectedProduct &&
            selectedPlan &&
            priceField?.value &&
            (!isAXINCategory || quantityField?.value)
          ) {
            // Find the cart product details for this specific product
            const productCartDetails = cartProductDetails?.find(
              (detail) => detail?.productId === selectedProduct.productId
            );

            // Prepare the update cart product payload
            const updateCartPayload = {
              billingType: getBillingTypeCodeFromPlanType(selectedPlan?.planDetails),
              planId: selectedPlan?.planId,
              productId: selectedProduct?.productId,
              quantity: quantity?.toString(),
              skuId: selectedPlan?.skuVariantId,
              buyerUserId: companyRegistrationDetails?.userId
            };

            // Call updateCartProduct API and then get the cart details
            dispatch(updateCartProduct(updateCartPayload))
              .then(() => {
                // Get cart product to retrieve cartId
                return dispatch(getCartProduct(companyRegistrationDetails?.userId));
              })
              .then((cartResponse) => {
                // Extract cartId from response
                if (cartResponse && cartResponse.length > 0) {
                  setCartId(cartResponse[0]?.cartId);
                }
              });

            const newProduct = {
              productId: selectedProduct.productId,
              productName: selectedProduct.productName,
              productDescription: selectedProduct.productShortDesc,
              planId: selectedPlan?.planId,
              itemId: selectedPlan?.itemId,
              categoryId: selectedPlan?.categoryId,
              skuVariantId: selectedPlan?.skuVariantId,
              price: parseInt(priceField.value) || 0,
              quantity: parseInt(quantity) || 1,
              tax: parseFloat(taxAmount) || 0,
              totalAmount: parseFloat(totalAmount) || 0,
              createdBy: productCartDetails?.createdBy,
              companyNo: productCartDetails?.companyNo,
              planDetails: selectedPlan?.planDetails,
              additionalInformation: additionalInformationField?.value || ''
            };

            setAddedProducts((prev) => {
              // Check if product with same productId and planId already exists
              const existingProductIndex = prev.findIndex(
                (product) =>
                  product.productId === selectedProduct.productId &&
                  product.planId === selectedPlan.planId
              );

              if (existingProductIndex !== -1) {
                // Overwrite existing product with new details
                const updatedProducts = [...prev];

                updatedProducts[existingProductIndex] = {
                  ...newProduct
                };

                return updatedProducts;
              }

              // Add new product if it doesn't exist
              return [...prev, newProduct];
            });

            // Clear the form fields after adding
            updateFormSections((field) => {
              if (field.name === FieldConstants.productName) {
                return { ...field, value: availableProducts[0] };
              }
              if (field.name === FieldConstants.price) {
                return { ...field, value: '', disabled: true };
              }
              if (field.name === FieldConstants.quantity) {
                return { ...field, value: '1', disabled: true };
              }
              if (field.name === FieldConstants.totalValue) {
                return { ...field, value: '0' };
              }
              if (field.name === FieldConstants.additionalInformation) {
                return { ...field, value: '' };
              }
              return field;
            });

            // Reset pricing API call ref to allow new product selection
            lastPricingApiCallRef.current = null;

            // Clear pricing policies from Redux to prevent auto-fill
            dispatch({
              type: 'GET_PRICING_DETAILS',
              payload: []
            });
          }
        }
        return;
      }

      // Handle manual quantity changes (restrict to numbers using isNaN)
      if (name === FieldConstants.quantity) {
        // Allow empty string (erasing the field) - will default to 0
        if (fieldValue === '' || fieldValue === null || fieldValue === undefined) {
          updateQuantityFields(0);
          setPlaceOrderForm((prev) => updateFieldValue(prev, name, '0', error));
          return;
        }

        const qtyValue = parseInt(fieldValue || value);
        // Block updates when the input is not a number
        if (isNaN(qtyValue)) {
          return;
        }
        updateQuantityFields(qtyValue);
        return; // Exit early to prevent bypassing validation below
      }

      // Only update error for price and quantity fields
      const errorToSet =
        name === FieldConstants.price || name === FieldConstants.quantity
          ? error
          : '';
      setPlaceOrderForm((prev) => updateFieldValue(prev, name, value, errorToSet));
    },
    [
      setPlaceOrderForm,
      updateFieldValue,
      updatePriceFields,
      updateQuantityFields,
      placeOrderForm,
      currencyFilteredProducts,
      updateFormSections,
      csmTenants?.tenantId,
      companyRegistrationDetails?.userId,
      dispatch,
      restoreApprovedBuyersList
    ]
  );

  const handleBlur = useCallback(
    ({ name, error }) => {
      // Only handle blur for price and quantity fields
      if (name === FieldConstants.price || name === FieldConstants.quantity) {
        setPlaceOrderForm((prev) =>
          prev.map((section) => ({
            ...section,
            fields: section.fields.map((field) =>
              field.name === name ? { ...field, error } : field
            )
          }))
        );
      }
    },
    [setPlaceOrderForm]
  );

  // Helper function for steps 3-5 of order processing
  const processCheckoutSteps = (productOrderId, checkoutId) => {
    // Step 3: Save checkout ID api
    return dispatch(
      saveCheckoutId(orderRequestPayloads.save(productOrderId, checkoutId))
    )
      .then(() => {
        // Step 4: Process address checkout
        return dispatch(
          processAddressCheckout(orderRequestPayloads.address(checkoutId))
        );
      })
      .then(() => {
        // Step 5: Initiate order process
        return dispatch(
          initiateOrderProcess(
            orderRequestPayloads.process(productOrderId, checkoutId)
          )
        );
      })
      .then((processOrderResult) => {
        // Step 6: Create order subscription in Order Fulfillment service
        const subscriptionPayload = orderRequestPayloads.orderSubscription(
          productOrderId,
          !isPostpaidPlan()
        );
        return dispatch(createOrderSubscription(subscriptionPayload)).then(
          () => processOrderResult
        );
      })
      .then((processOrderResult) => {
        if (processOrderResult) {
          // Delete cart products after successful order
          if (cartId && companyRegistrationDetails?.userId) {
            const cartKey = `${cartId}-${companyRegistrationDetails.userId}`;
            if (!deletedCartIdsRef.current.has(cartKey)) {
              dispatch(
                deleteCartProducts(cartId, companyRegistrationDetails.userId)
              );
              deletedCartIdsRef.current.add(cartKey);
            }
          }

          history.push('/order-success', {
            orderId: productOrderId,
            checkoutId: checkoutId
          });
        }
      });
  }; // step 2: Create checkout Id api
  const processCheckoutCreation = (productOrderId) => {
    return dispatch(
      createCheckoutId(orderRequestPayloads.checkout(productOrderId))
    ).then((checkoutResult) => {
      const checkoutId = checkoutResult?.checkoutId;
      return processCheckoutSteps(productOrderId, checkoutId);
    });
  };

  // Simple order processing function - all APIs called step by step
  const processOrder = () => {
    setIsProcessingOrder(true);

    // Step 1: Create product order
    dispatch(getProductOrderId(orderRequestPayloads.order()))
      .then((orderResult) => {
        const productOrderId = orderResult?.data?.productOrderId;
        return processCheckoutCreation(productOrderId);
      })
      .catch(() => {
        history.push('/order-failure');
      })
      .finally(() => {
        setIsProcessingOrder(false);
      });
  };

  const handlePlaceOrder = useCallback(() => {
    processOrder();
  }, [orderRequestPayloads]);

  const dialogButtons = [
    {
      label: content?.components?.place_order?.consent?.decline,
      mode: 'secondary',
      onClick: () => {
        dispatch(logout());
      }
    },
    {
      label: content?.components?.place_order?.consent?.accept,
      mode: 'primary',
      disabled: !isConsentChecked,
      onClick: () => {
        dispatch(
          acceptUserAgreementTerms(consentAcceptancePayload, accessRoles.csm)
        );
        localStorage.removeItem(idConstants.dpnLink);
      }
    }
  ];

  useEffect(() => {
    if (userAcceptedConsent) {
      localStorage.setItem(idConstants.consentAccepted, userAcceptedConsent);
    }
  }, [userAcceptedConsent]);

  const handleConsentCheckbox = (data) => {
    setIsConsentChecked(data.value);
  };

  return (
    <>
      {(isLoading || isProcessingOrder) && <Loader />}
      {user?.roles?.includes(accessRoles.csm) &&
        REGION_CODE !== REGION.IN.CODE.toLowerCase() && (
          <AccessDenied
            show={true}
            signOut={() => dispatch(logout())}
            details={content?.components?.login?.csm_access_denied}
          />
        )}
      {showConsent &&
        !isLoading &&
        REGION_CODE === REGION.IN.CODE.toLowerCase() &&
        localStorage.getItem(idConstants.consentAccepted) !== 'true' && (
          <div className={`csm-consent-order ${deviceClass}`}>
            <Dialog
              status='info'
              title={content?.components?.place_order?.consent?.title}
              show={showConsent && REGION_CODE === REGION.IN.CODE.toLowerCase()}
              showCloseIcon={false}
              buttons={dialogButtons}
            >
              <div className='checkbox-link-container'>
                <MpsCheckBox
                  id={content?.components?.place_order?.consent?.title}
                  displayName={
                    content?.components?.place_order?.consent?.description
                  }
                  checked={isConsentChecked}
                  handleChange={handleConsentCheckbox}
                />
                <div className='consent-link-container'>
                  <Link
                    className='-size-s consent-link'
                    to={localStorage.getItem(idConstants.dpnLink)}
                    label={
                      content?.components?.place_order?.consent
                        ?.data_protection_policy
                    }
                    target='_blank'
                  />
                </div>
              </div>
            </Dialog>
          </div>
        )}
      <div className={`place-order-form-container ${deviceClass}`}>
        <div className='form-header'>
          <h3>{content?.components?.place_order?.title}</h3>
        </div>
        <div className='form-body'>
          <MpsSectionedForm
            sections={formSectionsWithButtonState}
            handleInputChange={handleFormChange}
            handleBlur={handleBlur}
          />

          <div className='bulk-order-table'>
            <ChartGrid
              className='bulk-order-grid'
              gridProps={bulkOrderTableConfig.gridProps}
            />
          </div>

          {addedProducts.length > 0 && (
            <div className='gross-amount-section'>
              <div className='gross-amount-label'>
                {`${content?.components?.place_order?.form?.product_order_table?.gross_amount} (${currentCurrency})`}
              </div>
              <div className='gross-amount-value'>
                {addedProducts
                  .reduce((sum, product) => sum + product.totalAmount, 0)
                  .toFixed(2)}
              </div>
            </div>
          )}

          <div className='form-button'>
            <MpsButton
              mode='primary'
              label={content?.constants?.place_order}
              onClick={handlePlaceOrder}
              disabled={addedProducts.length === 0}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default PlaceOrder;
